import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthAction } from 'hooks/useAuthAction';
import { useEffect, useState } from 'react';
import { userAtom } from 'atoms/user';
import { useAtom } from 'jotai';
import { surveyAtom } from 'atoms/survey';
import {
  getSignupSurveyV1SurveysSignupGet,
  getUserMeV1UsersMeGet,
  partialUpdateUserSettingV1UsersSettingPatch,
  retrieveUserSettingV1UsersSettingGet,
  syncGoogleCalendarListV1EventsSyncingGoogleCalendarListGet,
} from 'queries';
import styled from '@emotion/styled';
import { LinearProgress, keyframes } from '@mui/material';

interface Props {
  isMobile?: boolean;
}

const fadeInOutAnimation = keyframes`
  from {
    opacity: 1;
  }
  50% {
    opacity: 1;
  }
  to {
    opacity: 1;
  }`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  animation: ${fadeInOutAnimation} ease-in-out 4s;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Auth = ({ isMobile = false }: Props) => {
  const navigate = useNavigate();
  const { login } = useAuthAction();
  const location = useLocation();
  const [, setSurvey] = useAtom(surveyAtom);
  const [, setUser] = useAtom(userAtom);
  const [render, setRender] = useState(false);

  useEffect(() => {
    const response = location.search;
    const fromPage = response.split('from=')[1] ? decodeURIComponent(response.split('from=')[1].split(/[&?]/)[0]) : '';
    const accessToken = response.split('access_token=')[1];
    const token = accessToken;
    setRender(true);

    localStorage.setItem('entrypoint', fromPage);

    if (!token) {
      navigate('/');
    } else {
      (async () => {
        const loginPromise = await login(token);
        const user = await getUserMeV1UsersMeGet();
        if (user) setUser(user);
        const survey = await getSignupSurveyV1SurveysSignupGet();
        if (survey) setSurvey(survey);
        const userSetting = await retrieveUserSettingV1UsersSettingGet();
        const timezone = userSetting.timezone || 'Asia/Seoul';
        if (!userSetting.timezone) {
          await partialUpdateUserSettingV1UsersSettingPatch({ timezone });
        }

        user.isCalendarPicked || survey.isTarget
          ? syncGoogleCalendarListV1EventsSyncingGoogleCalendarListGet()
          : await syncGoogleCalendarListV1EventsSyncingGoogleCalendarListGet();
        const minimumAnimationTime = new Promise((resolve) => setTimeout(resolve, 2000));
        const surveyTarget = survey?.isTarget;

        const [success] = await Promise.all([loginPromise, minimumAnimationTime]);

        if (success && user?.tutorialStatus !== undefined && surveyTarget !== undefined && user?.isCalendarPicked !== undefined) {
          if (isMobile && surveyTarget) {
            navigate('/m/survey');
          } else if (isMobile && !surveyTarget) {
            navigate('/m/home');
          } else if (surveyTarget) {
            navigate('/survey');
          } else if (!user?.isCalendarPicked) {
            navigate('/calendar');
          } else if (!user?.tutorialStatus && !localStorage.getItem('onboarding')) {
            navigate('/welcome');
          } else {
            navigate(`/${fromPage || 'task'}`);
          }
        }
      })();
    }
  }, []);

  return (
    <Container>
      {render && (
        <>
          <div style={{ width: '100%' }}>
            <LinearProgress />
          </div>
          <ImageWrapper>
            <svg width="320" height="122" viewBox="0 0 320 122" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M189.723 77.6061L190.256 77L189.723 77.6061Z" fill="#1F2023" />
              <path
                d="M319.005 10.75H303.538H302.884L302.763 11.3803L297.211 37.5379H270.593L276.072 11.7197L275.272 11.55L276.072 11.7197L276.266 10.75H275.272H259.829H259.175L259.029 11.3803L244.363 81.2227L244.145 82.2167H245.138H260.508H261.163L261.284 81.5621L267.514 52.0833H294.132L287.975 81.2227L287.781 82.2167H288.775H304.242H304.896L305.017 81.5621L304.242 81.3924L305.017 81.5621L319.781 11.7197L319.999 10.75H319.005Z"
                fill="#1F2023"
              />
              <path d="M37.7227 55.0085L37.7469 55.0328L38.0863 54.2812L37.7227 55.0085Z" fill="#1F2023" />
              <path
                d="M239.999 51.7416C239.054 49.487 237.672 47.5719 235.878 45.9961C234.181 44.4204 232.145 43.184 229.793 42.2385C227.466 41.2446 224.921 40.3961 222.157 39.6688C220.993 39.4022 219.442 39.087 217.575 38.6992C215.733 38.2628 213.915 37.681 212.121 36.9779C210.448 36.2264 208.993 35.3295 207.757 34.287C206.69 33.2688 206.157 32.0567 206.157 30.5779C206.157 29.0992 206.496 27.8628 207.103 26.9173C207.757 25.9234 208.606 25.1234 209.648 24.5416C210.739 23.887 211.951 23.4264 213.284 23.184C214.739 22.8688 216.145 22.7234 217.527 22.7234C219.224 22.7234 220.824 22.9173 222.327 23.281C223.781 23.5961 225.018 24.1295 226.012 24.881C227.054 25.6325 227.854 26.6264 228.387 27.8628V27.9113C228.969 29.0507 229.236 30.5537 229.187 32.4204V33.2446H229.987H245.381V32.4446C245.381 28.3234 244.654 24.7598 243.151 21.8264C241.721 18.8446 239.757 16.4204 237.236 14.5052C234.787 12.6143 231.927 11.2567 228.654 10.4567C225.454 9.584 222.036 9.17188 218.375 9.17188C214.909 9.17188 211.49 9.60824 208.121 10.4567C204.775 11.2567 201.769 12.5658 199.078 14.384C196.363 16.1537 194.157 18.4567 192.484 21.2931C190.763 24.1779 189.915 27.6688 189.915 31.6931C189.915 34.7719 190.424 37.4385 191.466 39.6688C192.557 41.8507 193.963 43.6931 195.66 45.1961L196.169 44.5901L195.636 45.1961C197.381 46.6749 199.345 47.887 201.503 48.8325C203.612 49.7537 205.793 50.5537 208.048 51.2082C210.206 51.8628 212.315 52.4931 214.424 53.0749L214.642 52.2992V52.3476L214.424 53.0749C216.533 53.6567 218.375 54.3113 219.951 55.087L220.266 54.384L219.927 55.087C221.551 55.8386 222.836 56.7355 223.781 57.7537C224.654 58.6264 225.115 59.7658 225.115 61.2931C225.115 62.9173 224.727 64.2507 223.975 65.3416V65.3658C223.272 66.5052 222.303 67.4264 221.115 68.1295L221.527 68.8325L221.09 68.1537C219.927 68.881 218.618 69.4385 217.115 69.8022C215.612 70.1901 214.109 70.3598 212.606 70.3598C210.448 70.3598 208.436 70.1658 206.618 69.8022C204.824 69.3658 203.296 68.687 202.012 67.7901C200.775 66.8931 199.806 65.7052 199.151 64.2022C198.496 62.6507 198.206 60.687 198.327 58.3113L198.375 57.4628H182.133V58.2628C182.133 66.9416 184.557 73.487 189.575 77.6567C194.618 81.8022 202.133 83.8143 211.999 83.8143C215.393 83.8143 218.812 83.3779 222.254 82.5295C225.721 81.7295 228.848 80.3961 231.612 78.5052C234.448 76.6143 236.751 74.1416 238.496 71.1113C240.339 68.0325 241.236 64.3234 241.236 60.0325C241.236 56.8325 240.969 54.0204 239.999 51.7416Z"
                fill="#1F2023"
              />
              <path
                d="M180.826 0.515625H157.674L157.432 0.879261L114.062 67.6672H81.9407L93.6741 11.7156L93.868 10.7459H76.7528L76.6074 11.3762L62.0377 81.2187L61.8438 82.1884H104.632L79.6134 120.71L78.8134 121.946H102.401L102.644 121.582L138.135 66.6247H161.65L163.807 81.4853L163.904 82.1884H180.826L180.68 81.2429L170.013 17.2914L180.05 1.75199L180.85 0.515625L180.826 0.515625ZM157.892 36.0308L160.05 53.5823H146.547L157.892 36.0308Z"
                fill="#1F2023"
              />
              <path
                d="M57.697 51.7416C56.7515 49.487 55.3697 47.5719 53.5758 45.9961C51.8788 44.4204 49.8424 43.184 47.4909 42.2385C45.1636 41.2446 42.6182 40.3961 39.8545 39.6688C38.6909 39.4022 37.1394 39.087 35.2727 38.6992C33.4303 38.2628 31.6121 37.681 29.8182 36.9779C28.1455 36.2264 26.6909 35.3295 25.4545 34.287C24.3879 33.2689 23.8545 32.0567 23.8545 30.5779C23.8545 29.0992 24.1939 27.8628 24.8 26.9173C25.4545 25.9234 26.303 25.1234 27.3455 24.5416C28.4364 23.887 29.6485 23.4264 30.9818 23.184C32.4364 22.8688 33.8424 22.7234 35.2242 22.7234C36.9212 22.7234 38.5212 22.9173 40.0242 23.281C41.4788 23.5961 42.7151 24.1295 43.7091 24.881C44.7515 25.6325 45.5515 26.6264 46.0848 27.8628V27.9113C46.6667 29.0507 46.9333 30.5537 46.8848 32.4204V33.2446H47.6848H63.0788V32.4446C63.0788 28.3234 62.3515 24.7598 60.8485 21.8264C59.4182 18.8688 57.4545 16.4204 54.9576 14.5052C52.5091 12.6143 49.6485 11.2567 46.3758 10.4567C43.1758 9.584 39.7576 9.17188 36.097 9.17188C32.6303 9.17188 29.2121 9.60824 25.8424 10.4567C22.497 11.2567 19.4909 12.5658 16.8 14.384C14.0848 16.1537 11.8788 18.4567 10.2061 21.2931C8.48485 24.1779 7.63636 27.6688 7.63636 31.6931C7.63636 34.7719 8.14545 37.4385 9.18788 39.6688C10.2788 41.8507 11.6848 43.6931 13.3818 45.1961C15.1273 46.6749 17.0909 47.887 19.2485 48.8325C21.3576 49.7537 23.5394 50.5537 25.7939 51.2082C27.9515 51.8628 30.0606 52.4931 32.1697 53.0749C34.2788 53.6567 36.1212 54.3113 37.697 55.087C39.3212 55.8386 40.6061 56.7355 41.5515 57.7537C42.4242 58.6264 42.8848 59.7658 42.8848 61.2931C42.8848 62.9173 42.497 64.2507 41.7455 65.3416V65.3658C41.0424 66.5052 40.0727 67.4264 38.8848 68.1295C37.7212 68.8567 36.4121 69.4143 34.9091 69.778C33.4061 70.1416 31.903 70.3355 30.4 70.3355C28.2424 70.3355 26.2303 70.1416 24.4121 69.778C22.6182 69.3416 21.0909 68.6628 19.8061 67.7658C18.5697 66.8689 17.6 65.681 16.9455 64.1779C16.2909 62.6264 16 60.6628 16.1212 58.287L16.1697 57.4386H0V58.2386C0 66.9173 2.42424 73.4628 7.44242 77.6325C12.4848 81.7779 20 83.7901 29.8667 83.7901C33.2606 83.7901 36.6788 83.3537 40.1212 82.5052C43.5879 81.7052 46.7151 80.3719 49.4788 78.481C52.3151 76.5901 54.6182 74.1173 56.3636 71.087C58.2061 68.0083 59.103 64.2992 59.103 60.0083C59.103 56.8083 58.6667 54.0204 57.697 51.7416Z"
                fill="#1F2023"
              />
              <path d="M221.235 68.0781L221.211 68.1024L221.647 68.7812L221.235 68.0781Z" fill="#1F2023" />
            </svg>
          </ImageWrapper>
        </>
      )}
    </Container>
  );
};

export default Auth;
